import { useEffect, useState } from 'react';
import { isToday } from 'date-fns';
import { Bell, X, ChevronLeft, ChevronRight } from 'lucide-react';
import useAlert from 'src/hooks/use-alert';
import {
  useUserNotificationsQuery,
  useUserReadAllNotificationsMutation,
} from 'src/lib/endpoints/dashboard/dashboard.api';
import NotificationCard from './NotificationCard';

const Section = ({ label, items }) =>
  items.length ? (
    <section>
      <div className='px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
        {label}
      </div>
      <div className='space-y-1'>
        {items.map((notification, idx) => (
          <NotificationCard notification={notification} key={notification?.id ?? idx} />
        ))}
      </div>
    </section>
  ) : null;

const Notification = ({ open, callback }) => {
  const [page, setPage] = useState(1);
  const { notifications } = useUserNotificationsQuery(page);
  const [lastPage, setLastPage] = useState(1);
  const {
    state: alertState,
    open: openAlert,
    close: closeAlert,
    Alert,
  } = useAlert();
  const { readNotifications } = useUserReadAllNotificationsMutation();

  const markAllAsRead = async () => {
    readNotifications(
      {},
      {
        onSuccess: async (res) => {
          openAlert({
            ...alertState,
            title: 'Notifications read',
            variant: 'success',
            active: true,
          });
        },
        onError: (error) => {
          openAlert({
            ...alertState,
            title: error?.response?.data?.message ?? error?.message ?? '',
            variant: 'error',
            active: true,
          });
        },
      }
    );
  };

  useEffect(() => {
    if (notifications) {
      setLastPage(notifications.lastPage);
    }
  }, [notifications]);

  if (!open) return null;

  const items = notifications?.data ?? [];
  const today = items.filter((n) =>
    n?.created_at ? isToday(new Date(n.created_at)) : false
  );
  const earlier = items.filter((n) =>
    n?.created_at ? !isToday(new Date(n.created_at)) : true
  );

  /* Above the z-50 tier (Navbar z-40, MobileMenu z-50, PageLoader z-[55]) —
     the drawer covers the header, so it has to outrank everything in the
     shell rather than tie with it. */
  return (
    <div className='fixed inset-0 z-[60]'>
      <button
        type='button'
        aria-label='Close notifications'
        onClick={callback}
        className='absolute inset-0 h-full w-full bg-brand-navy/40'
      />

      <div className='absolute right-0 top-0 flex h-full w-full flex-col bg-card shadow-ui-lg sm:max-w-md'>
        <div className='flex h-14 shrink-0 items-center justify-between gap-2 border-b px-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-brand-navy'>
            <Bell className='h-4 w-4 text-brand-orange' />
            Notifications
          </div>
          <div className='flex shrink-0 items-center gap-1'>
            {!!items.length && (
              <button
                type='button'
                onClick={markAllAsRead}
                className='text-xs font-semibold text-brand-orange hover:underline'
              >
                Mark all as read
              </button>
            )}
            <button
              type='button'
              onClick={callback}
              aria-label='Close'
              className='-mr-2 rounded-full p-2 text-brand-navy transition-colors hover:bg-surface'
            >
              <X className='h-5 w-5' />
            </button>
          </div>
        </div>

        <div className='flex-1 space-y-4 overflow-y-auto p-3'>
          {items.length ? (
            <>
              <Section label='Today' items={today} />
              <Section label='Earlier' items={earlier} />
            </>
          ) : (
            <div className='rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground'>
              You have no notifications yet.
            </div>
          )}
        </div>

        {lastPage > 1 && (
          <div className='flex shrink-0 items-center justify-between gap-2 border-t p-3'>
            <button
              type='button'
              disabled={page <= 1}
              onClick={() => setPage((prev) => (prev - 1 > 0 ? prev - 1 : prev))}
              className='inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-semibold text-brand-navy transition-colors hover:bg-surface disabled:pointer-events-none disabled:text-muted-foreground/40'
            >
              <ChevronLeft className='h-4 w-4' />
              Prev
            </button>
            <span className='text-[10px] uppercase tracking-wider tabular-nums text-muted-foreground'>
              Page {page} of {lastPage}
            </span>
            <button
              type='button'
              disabled={page >= lastPage}
              onClick={() =>
                setPage((prev) => (prev + 1 <= lastPage ? prev + 1 : prev))
              }
              className='inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-semibold text-brand-navy transition-colors hover:bg-surface disabled:pointer-events-none disabled:text-muted-foreground/40'
            >
              Next
              <ChevronRight className='h-4 w-4' />
            </button>
          </div>
        )}

        <Alert />
      </div>
    </div>
  );
};

export default Notification;
